// Which env pills the FeatureFlagsPage renders depends on which env the
// **ops console itself** is deployed in:
//
// - Prod ops console (the canonical control plane) exposes pills for the
//   real consumer environments: dev / staging / integration / prod. Ops
//   staff manage real-user behavior from here.
// - Each non-prod ops console (dev / staging / integration builds) shows
//   only its own "-test" pill. Those exist as sandboxes for exercising
//   the feature-flags system itself — toggling them has no effect on
//   real consumers unless a consumer is deliberately wired to send
//   context.environment="dev-test" / "staging-test" / "integration-test".
//
// The list returned here is also the *exact* set of keys that get
// written to enabledByEnvironment when a pill is toggled. The shared
// feature-flags API records them all in the same DDB record (one per
// flag) regardless of which ops console wrote them.

import { type EnvName, readEnvName } from "@/lib/env";

// Display order for the prod console's consumer-env pills (distinct from the
// canonical ENVIRONMENTS order in @/lib/env). Typed against EnvName so a
// removed environment is caught here.
const PROD_CONSUMER_ENVIRONMENTS: readonly EnvName[] = ["dev", "staging", "integration", "prod"];

/**
 * Resolve the list of env pills to render based on the ops console's
 * own build env. Exported as a function rather than a const so tests
 * can vary VITE_ENV_NAME between cases.
 */
export function getEnvironmentsForOpsBuild(): readonly string[] {
  const env = readEnvName();
  if (env === "prod") {
    return PROD_CONSUMER_ENVIRONMENTS;
  }
  // Non-prod ops console: only its own -test sandbox pill.
  return [`${env}-test`];
}
